import React, { useEffect, useMemo, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { getProjectById, getProjectsChronological } from '../data/mock';
import { kvToDisplay } from '../hooks/useProjects';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import Lightbox from '../components/Lightbox';
import {
  ArrowLeft,
  ArrowRight,
  Calendar,
  Tag,
  Wrench,
  Target,
  FileQuestion,
  ListChecks,
  TrendingUp,
  Lightbulb,
  Image as ImageIcon,
  Flag,
  Play,
  Expand,
} from 'lucide-react';

const sectionAccents = ['#4A8FD1', '#4BADC8', '#E4A135', '#4CB88A', '#D95871', '#4A8FD1', '#4BADC8'];

// Default icon rotation for dynamic-project sections so each kicker
// still gets a varied icon even though the section names are user-set.
const DYNAMIC_ICONS = [FileQuestion, Target, ListChecks, Wrench, TrendingUp, Lightbulb, ImageIcon];

const Section = ({ icon: Icon, title, children, number, accent = '#4A8FD1' }) => (
  <section className="py-10 border-t border-white/10 first:border-t-0">
    <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 lg:gap-10">
      <div className="lg:col-span-3">
        <div className="flex items-center gap-3 text-xs mono uppercase tracking-widest" style={{ color: accent }}>
          <span>{number}</span>
          <Icon className="w-4 h-4" />
        </div>
        <h2 className="mt-2 text-xl font-semibold text-slate-50">{title}</h2>
      </div>
      <div className="lg:col-span-9">{children}</div>
    </div>
  </section>
);

const ProjectDetail = () => {
  const { id } = useParams();
  const { t, language } = useLanguage();
  const navigate = useNavigate();
  const mockProject = getProjectById(id);
  const [dynProject, setDynProject] = useState(null);
  const [loadingDyn, setLoadingDyn] = useState(!mockProject);
  const [lbIndex, setLbIndex] = useState(null);

  // If the id isn't a mock project, try the dynamic API.
  useEffect(() => {
    if (mockProject) {
      setDynProject(null);
      setLoadingDyn(false);
      return;
    }
    let alive = true;
    setLoadingDyn(true);
    fetch(`/api/projects/${encodeURIComponent(id)}`)
      .then(async (r) => {
        const ct = r.headers.get('content-type') || '';
        if (!r.ok || !ct.includes('application/json')) return null;
        const j = await r.json();
        return j.ok ? j.project : null;
      })
      .then((p) => {
        if (alive) setDynProject(p ? kvToDisplay(p) : null);
      })
      .finally(() => alive && setLoadingDyn(false));
    return () => {
      alive = false;
    };
  }, [id, mockProject]);

  const project = mockProject || dynProject;

  // Open the lightbox at the media item that matches the thumbnail src.
  // Falls back to 0 if no exact match is found (keeps old behaviour).
  const openThumbnailLightbox = () => {
    if (!project?.media?.length) return;
    const match = project.media.findIndex(
      (m) => m.src === project.thumbnail || m.url === project.thumbnail,
    );
    setLbIndex(match >= 0 ? match : 0);
  };

  const { prev, next } = useMemo(() => {
    if (!mockProject) return { prev: null, next: null };
    const all = getProjectsChronological('asc');
    const idx = all.findIndex((p) => p.id === id);
    return {
      prev: idx > 0 ? all[idx - 1] : null,
      next: idx < all.length - 1 ? all[idx + 1] : null,
    };
  }, [id, mockProject]);

  if (loadingDyn) {
    return (
      <div className="max-w-3xl mx-auto px-5 py-24 text-center">
        <div className="text-slate-400">Loading…</div>
      </div>
    );
  }

  if (!project) {
    return (
      <div className="max-w-3xl mx-auto px-5 py-24 text-center">
        <h1 className="text-3xl font-bold text-slate-50">Project not found</h1>
        <Button
          onClick={() => navigate('/projects')}
          className="mt-6 bg-[#4A8FD1] hover:bg-[#6AAFE0]"
        >
          {t('projectDetail.back')}
        </Button>
      </div>
    );
  }

  // Lightbox needs `src` field — both mock and kvToDisplay output now have it.
  const lightboxItems = (project.media || []).map((m) => ({
    src: m.src || m.url,
    type: m.type || 'image',
    caption: m.caption || { en: '', de: '' },
  }));

  const isDynamic = !mockProject && !!dynProject;

  return (
    <div>
      {/* Hero */}
      <section className="relative border-b border-white/10 sky-section overflow-hidden">
        <div className="absolute top-0 left-0 right-0 h-[2px] cfd-bar" />
        <div className="relative max-w-6xl mx-auto px-5 sm:px-8 lg:px-12 pt-10 pb-14">
          <Link
            to="/projects"
            className="inline-flex items-center gap-2 text-sm font-medium text-slate-400 hover:text-[#4A8FD1] mb-8"
          >
            <ArrowLeft className="w-4 h-4" />
            {t('projectDetail.back')}
          </Link>

          <div className="flex flex-wrap items-center gap-3 text-[10px] mono uppercase tracking-widest text-slate-500 mb-4">
            <div className="flex items-center gap-1.5">
              <Calendar className="w-3.5 h-3.5" />
              {project.year}
            </div>
            <span className="w-1 h-1 rounded-full bg-slate-300" />
            <div className="flex items-center gap-1.5">
              <Tag className="w-3.5 h-3.5" />
              {(project.context || { en: '', de: '' })[language] || project.context?.en || ''}
            </div>
          </div>

          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-slate-50 leading-tight tracking-tight max-w-4xl">
            {project.title[language]}
          </h1>
          <p className="mt-4 text-lg text-slate-400 max-w-3xl leading-relaxed">
            {project.summary[language]}
          </p>
        </div>
      </section>

      {/* Main image — clickable to lightbox */}
      <section className="bg-[#1A2231]">
        <div className="max-w-6xl mx-auto px-5 sm:px-8 lg:px-12 pt-10">
          <button
            type="button"
            onClick={openThumbnailLightbox}
            data-testid="project-thumbnail-lightbox-btn"
            className="group relative block w-full aspect-[16/9] rounded-lg overflow-hidden bg-white/10 border border-white/10 hover:border-[#4A8FD1]/50 transition-colors"
          >
            <img
              src={project.thumbnail}
              alt={project.title[language]}
              className="w-full h-full object-cover"
            />
            {project.media?.length > 0 && (
              <span className="absolute top-3 right-3 flex items-center gap-1.5 px-2.5 py-1.5 rounded-md bg-[#10151F]/80 backdrop-blur border border-white/15 text-[10px] mono uppercase tracking-widest text-slate-200">
                <Expand className="w-3.5 h-3.5" />
                {language === 'de' ? 'Vergrößern' : 'Zoom'}
              </span>
            )}
          </button>
        </div>
      </section>

      {/* Case study */}
      <div className="max-w-6xl mx-auto px-5 sm:px-8 lg:px-12 py-10">
        {isDynamic ? (
          <>
            {(project.sections || []).map((s, idx) => {
              const Icon = DYNAMIC_ICONS[idx % DYNAMIC_ICONS.length];
              const accent = sectionAccents[idx % sectionAccents.length];
              const value = (s.value || {})[language] || s.value?.en || s.value?.de || '';
              return (
                <Section
                  key={idx}
                  icon={Icon}
                  number={String(idx + 1).padStart(2, '0')}
                  accent={accent}
                  title={(s.key || {})[language] || s.key?.en || s.key?.de || ''}
                >
                  <div className="text-slate-300 leading-relaxed text-base whitespace-pre-wrap">
                    {value}
                  </div>
                </Section>
              );
            })}
          </>
        ) : (
          <>
            <Section icon={FileQuestion} number="01" accent={sectionAccents[0]} title={t('projectDetail.problem')}>
              <p className="text-slate-300 leading-relaxed text-base">{project.problem[language]}</p>
            </Section>

            <Section icon={Target} number="02" accent={sectionAccents[1]} title={t('projectDetail.objective')}>
              <p className="text-slate-300 leading-relaxed text-base">{project.objective[language]}</p>
            </Section>

            <Section icon={ListChecks} number="03" accent={sectionAccents[2]} title={t('projectDetail.approach')}>
              <ol className="space-y-3">
                {project.approach[language].map((step, idx) => (
                  <li key={idx} className="flex gap-4 text-slate-300 leading-relaxed">
                    <span className="flex-shrink-0 w-7 h-7 rounded bg-white/10 text-[#E4A135] mono text-xs font-semibold flex items-center justify-center">
                      {String(idx + 1).padStart(2, '0')}
                    </span>
                    <span className="pt-1">{step}</span>
                  </li>
                ))}
              </ol>
            </Section>

            <Section icon={Wrench} number="04" accent={sectionAccents[3]} title={t('projectDetail.tools')}>
              <div className="flex flex-wrap gap-2">
                {project.tools.map((tool) => (
                  <Badge
                    key={tool}
                    variant="outline"
                    className="mono text-xs border-white/15 text-slate-300 px-3 py-1"
                  >
                    {tool}
                  </Badge>
                ))}
              </div>
            </Section>

            <Section icon={TrendingUp} number="05" accent={sectionAccents[4]} title={t('projectDetail.results')}>
              <ul className="space-y-3">
                {project.results[language].map((r, idx) => (
                  <li key={idx} className="flex gap-3 text-slate-300 leading-relaxed">
                    <span className="flex-shrink-0 mt-1.5 w-1.5 h-1.5 rounded-full bg-[#D95871]" />
                    <span>{r}</span>
                  </li>
                ))}
              </ul>
            </Section>

            <Section icon={Lightbulb} number="06" accent={sectionAccents[5]} title={t('projectDetail.learnings')}>
              <p className="text-slate-300 leading-relaxed text-base italic border-l-2 border-[#4A8FD1] pl-4">
                {project.learnings[language]}
              </p>
            </Section>
          </>
        )}

        {project.gallery && project.gallery.length > 0 && !project.media && (
          <Section icon={ImageIcon} number="07" accent={sectionAccents[6]} title={t('projectDetail.media')}>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {project.gallery.map((src, idx) => (
                <div
                  key={idx}
                  className="aspect-[4/3] rounded-lg overflow-hidden bg-white/10 border border-white/10"
                >
                  <img src={src} alt={`${project.title[language]} — ${idx + 1}`} className="w-full h-full object-cover" />
                </div>
              ))}
            </div>
          </Section>
        )}

        {project.media && project.media.length > 0 && (
          <Section icon={ImageIcon} number={isDynamic ? String((project.sections?.length || 0) + 1).padStart(2, '0') : '07'} accent={sectionAccents[6]} title={t('projectDetail.media')}>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
              {project.media.map((m, idx) => {
                const src = m.src || m.url;
                return (
                  <button
                    key={idx}
                    type="button"
                    onClick={() => setLbIndex(idx)}
                    className="group text-left bg-[#1A2231] border border-white/10 rounded-lg overflow-hidden hover:border-[#4A8FD1]/50 hover:shadow-lg transition-all"
                  >
                    <div className="aspect-[16/10] bg-white/5 overflow-hidden relative">
                      {m.type === 'image' ? (
                        <img
                          src={src}
                          alt={m.caption?.[language] || `${project.title[language]} — ${idx + 1}`}
                          className="w-full h-full object-contain transition-transform duration-500 group-hover:scale-[1.02]"
                          loading="lazy"
                        />
                      ) : (
                        <>
                          <video
                            src={src}
                            className="w-full h-full object-contain bg-black pointer-events-none"
                            preload="metadata"
                            muted
                            playsInline
                          />
                          <span className="absolute inset-0 flex items-center justify-center pointer-events-none">
                            <span className="w-14 h-14 rounded-full bg-[#10151F]/80 backdrop-blur border border-white/20 flex items-center justify-center group-hover:bg-[#4A8FD1]/90 group-hover:border-[#4A8FD1] transition-colors">
                              <Play className="w-5 h-5 text-slate-50 translate-x-[1px]" fill="currentColor" />
                            </span>
                          </span>
                        </>
                      )}
                      <span className="absolute top-2 left-2 text-[10px] mono uppercase tracking-widest px-2 py-0.5 rounded bg-[#10151F]/90 border border-white/15 text-slate-200">
                        {m.type === 'video' ? 'Video' : (language === 'de' ? 'Bild' : 'Image')} · {String(idx + 1).padStart(2, '0')}
                      </span>
                      <span className="absolute top-2 right-2 p-1.5 rounded bg-[#10151F]/90 border border-white/15 text-slate-200 opacity-0 group-hover:opacity-100 transition-opacity">
                        <Expand className="w-3.5 h-3.5" />
                      </span>
                    </div>
                    {m.caption?.[language] && (
                      <p className="px-4 py-3 text-xs text-slate-400 leading-relaxed border-t border-white/5">
                        {m.caption[language]}
                      </p>
                    )}
                  </button>
                );
              })}
            </div>
          </Section>
        )}

        {/* CONCLUSION — mock projects only; dynamic projects fold conclusion into sections */}
        {!isDynamic && project.conclusion && (
          <section className="mt-8 py-8 px-6 sm:px-8 rounded-lg border border-[#4A8FD1]/30 bg-gradient-to-br from-[#4A8FD1]/10 via-[#1A2231] to-[#1A2231]">
            <div className="flex items-center gap-3 text-xs mono uppercase tracking-widest text-[#4A8FD1] mb-4">
              <span>08</span>
              <Flag className="w-4 h-4" />
              <span className="w-8 h-px bg-[#4A8FD1]" />
            </div>
            <h2 className="text-2xl font-bold text-slate-50 mb-4">
              {t('projectDetail.conclusion')}
            </h2>
            <p className="text-slate-200 leading-relaxed text-base sm:text-lg">
              {project.conclusion[language]}
            </p>
          </section>
        )}
      </div>

      {/* Lightbox modal */}
      {lbIndex !== null && lightboxItems.length > 0 && (
        <Lightbox
          items={lightboxItems}
          index={lbIndex}
          language={language}
          escHint={t('projectDetail.escHint')}
          onClose={() => setLbIndex(null)}
          onNavigate={(i) => setLbIndex(i)}
        />
      )}

      {/* Prev / Next — mock projects only */}
      {!isDynamic && (
      <section className="border-t border-white/10 sky-section-soft">
        <div className="max-w-6xl mx-auto px-5 sm:px-8 lg:px-12 py-10 grid grid-cols-1 sm:grid-cols-2 gap-5">
          {prev ? (
            <Link
              to={`/projects/${prev.id}`}
              className="group p-5 bg-[#1A2231] border border-white/10 rounded-lg hover:border-[#4A8FD1] transition-colors"
            >
              <div className="flex items-center gap-2 text-xs mono uppercase tracking-widest text-slate-500 mb-2">
                <ArrowLeft className="w-3.5 h-3.5" />
                {t('projectDetail.prev')}
              </div>
              <div className="font-semibold text-slate-50 group-hover:text-[#4A8FD1]">
                {prev.title[language]}
              </div>
            </Link>
          ) : <div />}
          {next ? (
            <Link
              to={`/projects/${next.id}`}
              className="group p-5 bg-[#1A2231] border border-white/10 rounded-lg hover:border-[#4A8FD1] transition-colors text-right"
            >
              <div className="flex items-center justify-end gap-2 text-xs mono uppercase tracking-widest text-slate-500 mb-2">
                {t('projectDetail.next')}
                <ArrowRight className="w-3.5 h-3.5" />
              </div>
              <div className="font-semibold text-slate-50 group-hover:text-[#4A8FD1]">
                {next.title[language]}
              </div>
            </Link>
          ) : <div />}
        </div>
      </section>
      )}
    </div>
  );
};

export default ProjectDetail;
